import FieldsetRender from '@/lib/components/renders/fieldset';

import flowManager from '@/lib/services/flow-manager';
import toCamelCase from '@/lib/helpers/to-camel-case';

import store from '@/store';


const getValuesByName = (form) => {
  const { fields } = form;

  const toReturn = fields.reduce((red, field) => {
    const { name } = field;

    red[toCamelCase(name)] = store.state.formData.values[name];

    return red;
  }, {});

  return toReturn;
};

const renderUiSlots = (component) => {
  const { step } = component;
  const uiNameRegex = /ui-/i;

  const scopesToAdd = {};

  const filterAndAddToScopesFn = (
    objectScopes,
    callbackForAdd = scopeToAdd => scopeToAdd,
  ) => {
    Object.keys(objectScopes)
      .filter(val => uiNameRegex.test(val))
      .forEach((nameOfScope) => {
        const nameToUse = nameOfScope.replace(uiNameRegex, '');

        scopesToAdd[nameToUse] = callbackForAdd(objectScopes[nameOfScope]);
      });
  };

  filterAndAddToScopesFn(step.component.$slots);

  const contextToPassOnRender = {
    valuesByName: getValuesByName(step.form),
  };

  filterAndAddToScopesFn(
    step.component.$scopedSlots,
    scopeToAdd => scopeToAdd(contextToPassOnRender),
  );

  return scopesToAdd;
};

const contextForUiGen = (obj) => {
  let { mainAction } = obj.step;

  if (!mainAction) {
    mainAction = () => flowManager.goToNextStep();
  }

  const context = {
    disabledButton: obj.disabledButton,
    isInvalid: obj.isInvalid,
    isLoading: obj.isLoading,
    mainAction,
    isLastStep: obj.isLastStep,
    ...obj.step.uiProps,
  };

  return context;
};

export default {
  name: 'StepRender',

  components: {
    FieldsetRender,
  },

  props: {
    step: {
      type: Object,
      required: true,
    },
  },


  computed: {
    fieldsets() {
      return this.step.fieldsets;
    },

    canContinue() {
      return flowManager.canContinue;
    },

    disabledButton() {
      return this.step.isInvalid;
    },

    isInvalid() {
      return this.step.isInvalid;
    },

    ui() {
      return this.step.component.ui;
    },

    canEditValues() {
      return this.step.isEditable;
    },

    isLastStep() {
      return flowManager.isLastStep;
    },

    isLoading() {
      return this.step.isLoading;
    },
  },

  created() {
    this.step.$rendererComponent = this;
  },

  render(h) {
    const fieldsetsRenders = this.step.fieldsets.map(fieldset => h(
      FieldsetRender,
      {
        key: fieldset.id,
        props: {
          fieldset,
          canEditValues: this.canEditValues,
        },
      },
    ));

    const contextForUi = contextForUiGen(this);
    const uiSlotsRendered = renderUiSlots(this);
    /**
     * This part is in charge of rendering the default slot scope
     * in case that no fieldsets were found on the step
     */
    const childrenToMainUi = fieldsetsRenders.length > 0
      ? [...fieldsetsRenders] : [this.step.component.$slots.default];

    Object.keys(uiSlotsRendered).forEach((uiSlotName) => {
      const uiSlotRenderedToAdd = uiSlotsRendered[uiSlotName];

      childrenToMainUi.push(h(
        'div',
        {
          slot: uiSlotName,
        },
        [uiSlotRenderedToAdd],
      ));
    });

    const mainUi = h(
      this.ui,
      {
        tag: 'component',
        attrs: contextForUi,
      },
      childrenToMainUi,
    );

    return mainUi;
  },
};
